import React, { useEffect, useState } from 'react';
import { apiFetch } from "../../../../apiFetch";
import Calendar from 'react-calendar';
import 'react-calendar/dist/Calendar.css';
import jsPDF from 'jspdf';
import './MyJourney.css';

const MyJourney = ({ userId: propUserId }) => {
    const userId = propUserId || localStorage.getItem('playerId');

    const [selectedDate, setSelectedDate] = useState(null);
    const [showModal, setShowModal] = useState(false);
    const [title, setTitle] = useState('');
    const [story, setStory] = useState('');
    const [photos, setPhotos] = useState([]);
    const [entries, setEntries] = useState([]);
    const [view, setView] = useState('calendar');

    useEffect(() => {
        const fetchEntries = async () => {
            try {
                const res = await apiFetch(`/api/journey/${userId}`);

                if (!res.ok) {
                    const text = await res.text();
                    throw new Error(`Fetch journey failed: ${res.status} ${text}`);
                }

                const data = await res.json();

                // Sort by date ascending (oldest first)
                const sortedData = data.sort((a, b) => new Date(a.date) - new Date(b.date));
                setEntries(sortedData);
            } catch (err) {
                console.error('Error fetching journey entries:', err);
            }
        };

        if (userId) fetchEntries();
    }, [userId]);
    

    const handleDateClick = (date) => {
        setSelectedDate(date);
        setTitle('');
        setStory('');
        setPhotos([]);
        setShowModal(true);
    };

    const handlePhotoChange = (e) => {
        const files = Array.from(e.target.files);
        setPhotos(files);
    };

    const handleSave = async () => {
        console.log('📝 Saving entry with userId:', userId);

        try {
            const photoData = await Promise.all(
                photos.map(file => readFileAsDataURL(file))
            );

            const newEntry = {
                userId,
                date: selectedDate.toDateString(),
                title,
                story,
                photos: photoData,
            };

            const res = await apiFetch("/api/journey", {
                method: "POST",
                headers: { "Content-Type": "application/json" },
                body: JSON.stringify(newEntry),
            });

            if (!res.ok) {
                const text = await res.text();
                throw new Error(`Save journey failed: ${res.status} ${text}`);
            }


            const savedEntry = await res.json();
            setEntries([...entries, savedEntry]);
            setShowModal(false);
        } catch (err) {
            console.error('Error saving entry:', err);
        }
    };

    const handleDownloadPDF = async () => {
        const doc = new jsPDF();
        const pageWidth = doc.internal.pageSize.getWidth();
        const margin = 10;
        let y = 20;

        const mainTitle = 'My Journey on VSN';
        doc.setFontSize(22);
        doc.setFont('helvetica', 'bold');
        doc.setTextColor(0, 0, 0);

        const titleWidth = doc.getTextWidth(mainTitle);
        const x = (pageWidth - titleWidth) / 2;
        doc.text(mainTitle, x, y);
        doc.line(x, y + 2, x + titleWidth, y + 2);
        y += 15;

        for (const entry of entries) {
            doc.setFontSize(14);
            doc.setFont('helvetica', 'bold');
            doc.setTextColor(20, 20, 20);
            doc.text(`${entry.title} — ${entry.date}`, margin, y);
            y += 8;

            doc.setFontSize(12);
            doc.setFont('times', 'normal');
            doc.setTextColor(60, 60, 60);
            const storyLines = doc.splitTextToSize(entry.story, 180);
            doc.text(storyLines, margin, y);
            y += storyLines.length * 7;

            const imgWidth = 50;
            const imgHeight = 50;
            const spacing = 10;
            let xPos = margin;

            for (const base64 of entry.photos || []) {
                if (xPos + imgWidth > pageWidth - margin) {
                    xPos = margin;
                    y += imgHeight + spacing;
                }

                if (y + imgHeight > 270) {
                    doc.addPage();
                    y = 20;
                    xPos = margin;
                }

                doc.addImage(base64, 'JPEG', xPos, y, imgWidth, imgHeight);
                xPos += imgWidth + spacing;
            }

            y += imgHeight + 15;

            if (y > 270) {
                doc.addPage();
                y = 20;
            }
        }

        doc.save('MyFootballJourney.pdf');
    };

    const readFileAsDataURL = (file) => {
        return new Promise((resolve) => {
            const reader = new FileReader();
            reader.onload = () => resolve(reader.result);
            reader.readAsDataURL(file);
        });
    };

    return (
        <div className="my-journey-wrapper">
            <div className="journey-toggle-container">
                <button
                    onClick={() => setView('calendar')}
                    className={`journey-toggle-btn ${view === 'calendar' ? 'active' : ''}`}
                >
                    The Journey
                </button>
                <button
                    onClick={() => setView('blog')}
                    className={`journey-toggle-btn ${view === 'blog' ? 'active' : ''}`}
                >
                    My Story
                </button>
            </div>

            {view === 'calendar' ? (
                <div className="calendar-wrapper">
                    <div className="calendar-instruction">
                        Click a date to start documenting your Journey
                    </div>
                    <Calendar onClickDay={handleDateClick} />
                </div>
            ) : (
                <div className="journey-blog">
                    {entries.length > 0 && (
                        <button className="download-btn" onClick={handleDownloadPDF}>
                            Download My Story as PDF
                        </button>
                    )}
                    {entries.map((entry, index) => (
                        <div key={index} className="entry-card">
                            <h4>{entry.title} <span>{entry.date}</span></h4>
                            <p>{entry.story}</p>
                            <div className="photo-preview">
                                {entry.photos?.map((photo, i) => (
                                    <img
                                        key={i}
                                        src={photo}
                                        alt={`entry-${index}-img-${i}`}
                                    />
                                ))}
                            </div>
                        </div>
                    ))}
                </div>
            )}

            {showModal && (
                <div className="modal-overlay">
                    <div className="modal-content">
                        <h3>{selectedDate?.toDateString()}</h3>
                        <input
                            type="text"
                            placeholder="Activity Title"
                            value={title}
                            onChange={(e) => setTitle(e.target.value)}
                        />
                        <textarea
                            placeholder="What happened today? Did you attend a Trial? Signed for a club? Scout reached out on VSN — or did you just drink water? It’s your story. Tell it in your own way."
                            value={story}
                            onChange={(e) => setStory(e.target.value)}
                            rows={5}
                        />
                        <input
                            type="file"
                            multiple
                            onChange={handlePhotoChange}
                        />
                        <div className="modal-actions">
                            <button className="save-btn" onClick={handleSave}>Save</button>
                            <button className="cancel-btn" onClick={() => setShowModal(false)}>Cancel</button>
                        </div>
                    </div>
                </div>
            )}
        </div>
    );
};

export default MyJourney;








